import React from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import TableSortLabel from '@material-ui/core/TableSortLabel'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import DeleteIcon from '@material-ui/icons/Delete'
import SaveIcon from '@material-ui/icons/SaveAlt'
import FilterListIcon from '@material-ui/icons/FilterList'
import { lighten } from '@material-ui/core/styles/colorManipulator'
import Button from '@material-ui/core/Button'


function desc(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1
    }
    if (b[orderBy] > a[orderBy]) {
        return 1
    }
    
    return 0
}

function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index])
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0])
        if (order !== 0) return order
        return a[1] - b[1]
    })
    return stabilizedThis.map(el => el[0])
}

function getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const headRows = [
    { id: 'name', numeric: false, disablePadding: false, label: 'File Name', sortable: true },
    { id: 'created', numeric: false, disablePadding: false, label: 'Created', sortable: true },
    { id: 'category', numeric: false, disablePadding: false, label: 'Category', sortable: true },
    { id: 'actions', numeric: false, disablePadding: false, label: 'Actions', sortable: false },
]

function EnhancedTableHead(props) {
    const classes = useStyles()
    const { order, orderBy, rowCount, onRequestSort } = props
    const createSortHandler = property => event => {
        onRequestSort(event, property)
    }

    return (
        <TableHead>
            <TableRow>
            { headRows.map(row => (
                <TableCell
                    key={row.id}
                    align={row.numeric ? 'right' : 'left'}
                    padding={row.disablePadding ? 'none' : 'default'}
                    sortDirection={orderBy === row.id ? order : false}
                    className={ classes.table_header_cell }
                >
                    { row.sortable &&
                        <TableSortLabel
                        active={orderBy === row.id}
                        direction={order}
                        onClick={createSortHandler(row.id)}
                        >
                            { row.label }
                        </TableSortLabel>
                    }
                    { !row.sortable &&
                        row.label
                    }
                </TableCell>
            ))}
            </TableRow>
        </TableHead>
    )
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
}

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
    },
    paper: {
        width: '100%',
        boxShadow: 'none',
    },
    table: {
        minWidth: 750,
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    row: {
        paddingTop: 2,
        paddingBottom: 2,
    },
    icon: {
        fontSize: 16,
    },
    buttonPadding: {
        padding: 6,
    },
    table_header_cell: {
        fontWeight: 500,
        fontSize: 12,
        paddingLeft: 1,
        '&:last-child': {
            paddingRight: 1,
        },
        color: 'rgba(0, 0, 0, 0.7)',
    },
    table_cell: {
        paddingTop: 3,
        paddingBottom: 3,
        paddingLeft: 1,
        '&:last-child': {
            paddingRight: 1,
        },
        color: 'rgba(0, 0, 0, 0.7)',
    },
}))

function DocumentsTable(props) {

    const { documentListData, deleteDocument, documentsLoading } = props

    const classes = useStyles()
    const [order, setOrder] = React.useState('asc')
    const [orderBy, setOrderBy] = React.useState('category')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(25)

    function handleRequestSort(event, property) {
        const isDesc = orderBy === property && order === 'desc'
        setOrder(isDesc ? 'asc' : 'desc')
        setOrderBy(property)
    }

    function handleChangePage(event, newPage) {
        setPage(newPage);
    }

    function handleChangeRowsPerPage(event) {
        setRowsPerPage(+event.target.value);
    }

    function handleDeleteDocument(member_id, document_id) {

        let payload = {
            member_id: member_id,
            document_id: document_id
        }

        deleteDocument(payload)
    }

    const emptyRows = rowsPerPage - Math.min(rowsPerPage, documentListData.length - page * rowsPerPage);
    
    return (
        <div className={classes.root}>

            { !documentsLoading && documentListData.length === 0 &&
                <Typography>
                    Fund has no documents
                </Typography>
            }

            { documentListData.length > 0 &&
                <Paper className={classes.paper}>
                    <div className={classes.tableWrapper}>
                        <Table
                            className={classes.table}
                            aria-labelledby="tableTitle"
                            size="small"
                        >
                            <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={documentListData.length}
                            />
                            <TableBody>
                                { stableSort(documentListData, getSorting(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => {

                                    return (
                                        <TableRow hover tabIndex={-1} key={ row.id }>
                                            <TableCell className={ classes.table_cell } scope="row">{ row.file_name }</TableCell>
                                            <TableCell className={ classes.table_cell }>{ row.created }</TableCell>
                                            <TableCell className={ classes.table_cell }>{ row.category }</TableCell>
                                            <TableCell className={ classes.table_cell }>
                                                <Tooltip className={ classes.buttonPadding } title="Download">
                                                    <IconButton aria-label="Download" target="_blank" href={`${process.env.REACT_APP_WEBSITE_URL}` + row.download_url}>
                                                        <SaveIcon className={ classes.icon } />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip className={ classes.buttonPadding } title="Delete">
                                                    <IconButton aria-label="Delete" onClick={() => handleDeleteDocument(row.user_id, row.id)}>
                                                        <DeleteIcon className={ classes.icon } />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                                {/*emptyRows > 0 && (
                                    <TableRow style={{ height: 49 * emptyRows }}>
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )*/}
                            </TableBody>
                        </Table>
                    </div>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={documentListData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        backIconButtonProps={{
                        'aria-label': 'Previous Page',
                        }}
                        nextIconButtonProps={{
                        'aria-label': 'Next Page',
                        }}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </Paper>
            }
            {/*
            <Button component="span" className={classes.button}>
                Upload a document
            </Button>
            */}
        </div>
    )
}

export default DocumentsTable;


// WEBPACK FOOTER //
// ./src/components/DocumentsTable/index.js